import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Button, Col, Input, Row, Select } from 'antd';
import { useUserContext } from '../../../contexts/UserContext';
import { useForm } from 'antd/es/form/Form';
import { trimValues } from '../../../utils/trimValues';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { Plus } from 'react-bootstrap-icons';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import { useTranslation } from 'react-i18next';
import { basketService } from '../../../api/services/basketService';
import type { Basket } from '../../../types/basket';
import AddBasketForm from '../../../components/basket/AddBasket';
import BasketTable from '../../../components/basket/BasketTable';
import ProductAssignmentsModal from '../../../components/basket/ProductAssignmentsModal';
import { ActiveFilterEnum } from '../../../enums/ActiveFilterEnum';
import FormModelComponent from '../../../components/model/FormModelComponent';
import { mapUrlsToAttachmentField } from '../../../utils/mapUrlsToAttachmentField';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import { normalize } from '../../../utils/normalizeWords';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import { PermissionEnum } from '../../../enums/PermissionEnum';
import { usePermissions } from '../../../hooks/usePermissions';

type BasketLogoValue =
  | string
  | { image?: string | null; dataUrl?: string | null; url?: string | null };

interface BasketProductAssignmentFormValue {
  product: string | number;
  quantityAssigned: string | number;
  unit?: string;
  products?: Array<string | number>;
}

interface BasketFormValues {
  name: string;
  description?: string;
  type?: BasketLogoValue[];
  productAssignments?: BasketProductAssignmentFormValue[];
}

const getNumberList = (values: Array<string | number> = []) =>
  values
    .filter((value) => value !== null && value !== undefined && value !== '')
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

const getBasketLogoPayloadValue = (
  values: BasketFormValues,
  currentLogo?: string | null
): string | null | undefined => {
  const logoItem = values.type?.[0];

  if (!logoItem) {
    return null;
  }

  const logoValue = typeof logoItem === 'string' ? logoItem : logoItem.image;

  if (!logoValue) {
    return null;
  }

  if (
    currentLogo &&
    typeof logoItem === 'object' &&
    (logoItem.image === currentLogo ||
      logoItem.url === currentLogo ||
      logoItem.dataUrl === currentLogo)
  ) {
    return undefined;
  }

  return logoValue;
};

const getBasketMutationPayload = (values: BasketFormValues, logo: string | null | undefined) => {
  const payload = {
    name: values.name,
    description: values.description,
    mainProducts: (values.productAssignments ?? [])
      .filter((assignment) => Number.isFinite(Number(assignment.product)))
      .map((assignment) => ({
        id: Number(assignment.product),
        amount: Number(assignment.quantityAssigned),
        unit: assignment.unit,
        products: getNumberList(assignment.products),
      })),
  };

  return logo === undefined ? payload : { ...payload, logo };
};

const getBasketFormAssignments = (basket: Basket | Record<string, any>) => {
  const basketRecord = basket as Record<string, any>;

  if (Array.isArray(basketRecord.productAssignments)) {
    return basketRecord.productAssignments;
  }

  return (basketRecord.mainProducts ?? []).map((mainProduct: any) => ({
    product: mainProduct.id ?? mainProduct.mainProduct?.id,
    quantityAssigned: mainProduct.amount,
    unit: mainProduct.unit,
    products: (mainProduct.products ?? [])
      .filter((product: any) => typeof product !== 'object' || product?.isActive !== false)
      .map((product: any) => (typeof product === 'object' ? product.id : product)),
  }));
};

const normalizeBasketForTable = (basket: any): Basket => ({
  ...basket,
  productCount:
    basket?.productCount ??
    (basket?.mainProducts ?? []).reduce(
      (count: number, mainProduct: any) => count + (mainProduct.products?.length ?? 0),
      0
    ),
});

const getBasketFromIndexedDbResult = (basketResult: any): Basket | null => {
  if (Array.isArray(basketResult)) {
    return (basketResult[0]?.data ?? basketResult[0] ?? null) as Basket | null;
  }

  return (basketResult?.data ?? basketResult ?? null) as Basket | null;
};

const BasketView: React.FC = () => {
  const [addBasketForm] = useForm();
  const { t } = useTranslation(['basket', 'common']);

  const [authStatus, setAuthStatus] = useState<{ isAuthenticated: boolean; token: string | null }>({
    isAuthenticated: false,
    token: null,
  });

  const [isBasketModalOpen, setIsBasketModalOpen] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [selectedBasketId, setSelectedBasketId] = useState<number | null>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  const [isConfirmClicked, setIsConfirmClicked] = useState<boolean>(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
  const [basketData, setBasketData] = useState<Basket[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [basketSearch, setBasketSearch] = useState<string>('');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [isLoadingFilterData, setIsLoadingFilterData] = useState<boolean>(false);
  const [isLoadingBaskets, setIsLoadingBaskets] = useState<boolean>(false);
  const [enableClearFiltersBtn, setEnableClearFiltersBtn] = useState<boolean>(false);
  const [isEditDataLoading, setIsEditDataLoading] = useState<boolean>(false);
  const [isAssignmentsModalOpen, setIsAssignmentsModalOpen] = useState<boolean>(false);
  const [isAssignmentsLoading, setIsAssignmentsLoading] = useState<boolean>(false);
  const [selectedAssignmentBasket, setSelectedAssignmentBasket] = useState<Basket | null>(null);
  const [statusFilter, setStatusFilter] = useState<ActiveFilterEnum>(ActiveFilterEnum.ACTIVE);
  const [isRestoreConfirmationOpen, setIsRestoreConfirmationOpen] = useState<boolean>(false);
  const [basketRefreshRequest, setBasketRefreshRequest] = useState<{
    key: number;
    page: number;
    filters: { search?: string; products?: string[]; status?: string };
  } | null>(null);

  const { successNotification, errorNotification } = useNotificationContext();
  const { IsAuthenticated, getUserToken } = useUserContext();
  const { isOnline } = useIsOnlineContext();
  const { hasPermission } = usePermissions();
  const canManageBaskets = hasPermission(PermissionEnum.MANAGE_BASKETS);

  const getFilteredLocalBaskets = useCallback(
    async (filters: { search?: string; products?: string[] } = {}) => {
      const basketsData = await referenceDataSyncService.getBaskets();
      let filteredBaskets = basketsData
        .map(normalizeBasketForTable)
        .filter((basket) => basket?.isActive !== false)
        .sort((a, b) => Number(b.createTime || 0) - Number(a.createTime || 0));

      if (filters.search?.trim()) {
        const searchText = normalize(filters.search).toString().toLowerCase();
        filteredBaskets = filteredBaskets.filter((basket) =>
          [basket.name, basket.description]
            .filter(Boolean)
            .some((value) => normalize(String(value)).toString().toLowerCase().includes(searchText))
        );
      }

      if (filters.products?.length) {
        filteredBaskets = filteredBaskets.filter((basket) =>
          (basket.mainProducts ?? []).some((mainProduct: any) =>
            (mainProduct.products ?? []).some((product: any) =>
              filters.products?.includes(product?.refId ?? product)
            )
          )
        );
      }

      return filteredBaskets;
    },
    []
  );

  const loadLocalBasketList = useCallback(
    async (page: number, filters: any = {}, pageLimit: number = pageSize) => {
      const filteredBaskets = await getFilteredLocalBaskets(filters);
      const offset = (page - 1) * pageLimit;

      setBasketData(filteredBaskets.slice(offset, offset + pageLimit));
      setTotal(filteredBaskets.length);
      setCurrentPage(page);
    },
    [getFilteredLocalBaskets, pageSize]
  );

  const getAllBasketList = useCallback(
    async (page: number = 1, filters: any = {}, customPageSize?: number) => {
      setIsLoadingBaskets(true);
      const hasClientFilters = Boolean(filters?.products?.length);
      const search = filters?.search?.trim();
      const pageLimit = customPageSize !== undefined ? customPageSize : pageSize;
      const requestLimit = hasClientFilters ? 100 : pageLimit;

      try {
        if (!isOnline) {
          await loadLocalBasketList(page, filters, pageLimit);
          return;
        }

        const response = await basketService.getBaskets({
          page: hasClientFilters ? 1 : page,
          limit: requestLimit,
          sortBy: 'createTime',
          sortOrder: 'DESC',
          search,
          status: filters?.status || 'active',
        });

        let basketsList = response.data.map(normalizeBasketForTable);

        if (filters?.products?.length) {
          basketsList = basketsList.filter((basket) =>
            (basket.mainProducts ?? []).some((mainProduct: any) =>
              (mainProduct.products ?? []).some((product: any) =>
                filters.products.includes(product?.refId ?? product)
              )
            )
          );
        }

        if (hasClientFilters) {
          const offset = (page - 1) * pageLimit;
          setBasketData(basketsList.slice(offset, offset + pageLimit));
          setTotal(basketsList.length);
        } else {
          setBasketData(basketsList);
          setTotal(response.total);
        }

        setCurrentPage(page);
      } catch (error) {
        console.error('Failed to fetch baskets from backend:', error);
        await loadLocalBasketList(page, filters, pageLimit);
      } finally {
        setIsLoadingBaskets(false);
      }
    },
    [isOnline, loadLocalBasketList, pageSize]
  );

  const handlePageChange = (page: number, size?: number) => {
    const newPageSize = size && size !== pageSize ? size : pageSize;

    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }

    getAllBasketList(page, { search: basketSearch, products: selectedProducts, status: statusFilter }, newPageSize);
  };

  const fetchFilterData = useCallback(async () => {
    setIsLoadingFilterData(true);
    try {
      const productsData = await referenceDataSyncService.getPresentations();
      setProducts([...productsData].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error fetching filter data:', error);
    } finally {
      setIsLoadingFilterData(false);
    }
  }, []);

  const requestBasketRefresh = useCallback(
    (
      page: number = currentPage,
      filters = { search: basketSearch, products: selectedProducts, status: statusFilter }
    ) => {
      setBasketRefreshRequest((previousRequest) => ({
        key: (previousRequest?.key ?? 0) + 1,
        page,
        filters,
      }));
    },
    [basketSearch, currentPage, selectedProducts, statusFilter]
  );

  useEffect(() => {
    const token = getUserToken();
    const isAuth = IsAuthenticated();
    setAuthStatus({ isAuthenticated: isAuth, token });
  }, [IsAuthenticated, getUserToken]);

  useEffect(() => {
    fetchFilterData();
  }, [fetchFilterData]);

  useEffect(() => {
    if (!basketRefreshRequest) return;

    const refreshBasketData = async () => {
      await fetchFilterData();
      await getAllBasketList(basketRefreshRequest.page, basketRefreshRequest.filters);
    };

    refreshBasketData();
  }, [basketRefreshRequest, fetchFilterData, getAllBasketList]);

  useEffect(() => {
    const filters = {
      search: basketSearch,
      products: selectedProducts,
      status: statusFilter,
    };
    const hasFilters = basketSearch.trim().length > 0 || selectedProducts.length > 0 || statusFilter !== 'active';

    getAllBasketList(1, filters);
    setEnableClearFiltersBtn(hasFilters);
  }, [basketSearch, getAllBasketList, selectedProducts, statusFilter]);

  const clearAllFilters = () => {
    setBasketSearch('');
    setSelectedProducts([]);
    setStatusFilter(ActiveFilterEnum.ACTIVE);
    getAllBasketList(1, { status: ActiveFilterEnum.ACTIVE });
  };

  // Reset status filter to 'active' when user goes offline
  useEffect(() => {
    if (!isOnline) {
      setStatusFilter(ActiveFilterEnum.ACTIVE);
    }
  }, [isOnline]);

  const handleCancel = () => {
    addBasketForm.resetFields();
    setIsBasketModalOpen(false);
  };

  const getConfirmation = async () => {
    try {
      await addBasketForm.validateFields();
      setIsConfirmationOpen(true);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCreate = async () => {
    try {
      setIsConfirmClicked(true);

      const values = (await addBasketForm.validateFields()) as BasketFormValues;
      setIsConfirmationOpen(false);
      setIsBasketModalOpen(false);
      setIsLoadingBaskets(true);

      const trimmedValues = trimValues(values) as BasketFormValues;
      addBasketForm.setFieldsValue(trimmedValues);

      const selectedBasket = isEdit
        ? basketData.find((basket) => basket.id === selectedBasketId)
        : undefined;
      const processedLogo = getBasketLogoPayloadValue(
        trimmedValues,
        isEdit ? selectedBasket?.logo : undefined
      );

      const basketPayload = getBasketMutationPayload(trimmedValues, processedLogo);

      if (!isOnline) {
        errorNotification(t(isEdit ? 'basketUpdateFail' : 'basketCreateFail'));
        return;
      }

      if (isEdit) {
        try {
          if (!selectedBasket?.id) {
            throw new Error(t('basketUpdateFail'));
          }

          await basketService.updateBasket(Number(selectedBasket.id), basketPayload);
          requestBasketRefresh(currentPage, {
            search: basketSearch,
            products: selectedProducts,
            status: statusFilter,
          });

          addBasketForm.resetFields();
          addBasketForm.setFields([]);
          successNotification(t('basketUpdateSuccess'));
        } catch (error: any) {
          console.error('Failed to update basket:', error);
          errorNotification(`${t(error?.message)}`);
        }
      } else {
        try {
          const result = await basketService.createBasket(basketPayload);

          if (result) {
            requestBasketRefresh(1, {
              search: basketSearch,
              products: selectedProducts,
              status: statusFilter,
            });

            addBasketForm.resetFields();
            addBasketForm.setFields([]);
            successNotification(t('basketCreateSuccess'));
          }
        } catch (error: any) {
          console.error('Failed to create basket:', error);
          errorNotification(`${t(error?.message)}`);
        }
      }
    } catch (error) {
      console.error('Form validation failed in handleCreate:', error);
      errorNotification(t('basketCreateFail'));
    } finally {
      setIsLoadingBaskets(false);
      setIsConfirmClicked(false);
    }
  };

  const getDeleteConfirmation = (id: number) => {
    setIsDeleteConfirmationOpen(true);
    setSelectedBasketId(id);
  };

  const handleDelete = async () => {
    try {
      setIsConfirmClicked(true);
      setIsDeleteConfirmationOpen(false);
      setIsLoadingBaskets(true);

      const selectedBasket = basketData.find((basket) => basket.id === selectedBasketId);

      if (!isOnline || !selectedBasket?.id) {
        throw new Error(t('basketDeleteFail'));
      }

      const isDeleted = await basketService.deleteBasket(Number(selectedBasket.id));

      if (!isDeleted) {
        throw new Error(t('basketDeleteFail'));
      }

      requestBasketRefresh(currentPage, {
        search: basketSearch,
        products: selectedProducts,
        status: statusFilter,
      });

      successNotification(t('basketDeleteSuccess'));
    } catch (error: any) {
      console.error('Failed to delete basket:', error);
      setIsDeleteConfirmationOpen(false);
      errorNotification(error?.message || t('basketDeleteFail'));
    } finally {
      setIsConfirmClicked(false);
      setIsLoadingBaskets(false);
    }
  };

  const getRestoreConfirmation = (id: number) => {
    setIsRestoreConfirmationOpen(true);
    setSelectedBasketId(id);
  };

  const cancelRestoreConfirmation = () => {
    setIsRestoreConfirmationOpen(false);
  };

  const handleRestore = async () => {
    try {
      setIsConfirmClicked(true);
      setIsRestoreConfirmationOpen(false);
      setIsLoadingBaskets(true);

      const selectedBasket = basketData.find((basket) => basket.id === selectedBasketId);

      if (!isOnline || !selectedBasket?.id) {
        throw new Error(t('basketRestoreFail'));
      }

      const isRestored = await basketService.restoreBasket(Number(selectedBasket.id));

      if (!isRestored) {
        throw new Error(t('basketRestoreFail'));
      }

      requestBasketRefresh(currentPage, {
        search: basketSearch,
        products: selectedProducts,
        status: statusFilter,
      });

      successNotification(t('basketRestoreSuccess'));
    } catch (error: any) {
      console.error('Failed to restore basket:', error);
      setIsRestoreConfirmationOpen(false);
      errorNotification(error?.message || t('basketRestoreFail'));
    } finally {
      setIsConfirmClicked(false);
      setIsLoadingBaskets(false);
    }
  };

  const handleViewProductAssignments = async (id: number) => {
    try {
      setIsAssignmentsLoading(true);
      setIsAssignmentsModalOpen(true);

      const basketFromCurrentRows = basketData.find((basket) => basket.id === id);
      if (basketFromCurrentRows) {
        const basketDetails =
          isOnline && basketFromCurrentRows?.mainProducts
            ? basketFromCurrentRows
            : getBasketFromIndexedDbResult(
                await referenceDataSyncService.getBasketDataById(basketFromCurrentRows.id)
              );

        if (!basketDetails) {
          throw new Error('Basket not found');
        }

        setSelectedAssignmentBasket(normalizeBasketForTable(basketDetails));
      }
    } catch (error) {
      console.error('Failed to load basket product assignments:', error);
      errorNotification(t('productAssignmentsLoadFail'));
      setIsAssignmentsModalOpen(false);
    } finally {
      setIsAssignmentsLoading(false);
    }
  };

  const closeAssignmentsModal = () => {
    setIsAssignmentsModalOpen(false);
    setSelectedAssignmentBasket(null);
  };

  const handleEdit = async (id: number) => {
    try {
      setSelectedBasketId(id);
      setIsBasketModalOpen(true);
      setIsEditDataLoading(true);

      let selectedBasketData: any = null;

      const basketFromCurrentRows = basketData.find((basket) => basket.id === id);

      if (basketFromCurrentRows) {
        const indexedDbBasketData = basketFromCurrentRows.id
          ? getBasketFromIndexedDbResult(
              await referenceDataSyncService.getBasketDataById(basketFromCurrentRows.id)
            )
          : null;

        selectedBasketData = isOnline
          ? {
              ...(indexedDbBasketData ?? {}),
              ...basketFromCurrentRows,
              mainProducts: basketFromCurrentRows.mainProducts ?? indexedDbBasketData?.mainProducts,
            }
          : (indexedDbBasketData ?? basketFromCurrentRows);

        setSelectedAssignmentBasket(normalizeBasketForTable(selectedBasketData));
      }

      if (!selectedBasketData) {
        throw new Error('Basket not found');
      }

      const imageConverted = selectedBasketData.logo
        ? mapUrlsToAttachmentField([selectedBasketData.logo])
        : [];

      const formData = {
        ...selectedBasketData,
        productAssignments: getBasketFormAssignments(selectedBasketData ?? {}),
        type: imageConverted,
        logoVal: imageConverted,
      };

      addBasketForm.setFieldsValue(formData);
      setIsEditDataLoading(false);
      setIsEdit(true);
    } catch (error) {
      console.error('Failed to fetch basket data for editing:', error);
      errorNotification('Failed to load basket details for editing');
      setIsEditDataLoading(false);
    }
  };

  const handleNewBasketButton = () => {
    setIsEdit(false);
    setSelectedBasketId(null);
    addBasketForm.resetFields();
    addBasketForm.setFields([]);
    setIsConfirmationOpen(false);
    setIsBasketModalOpen(true);
  };

  const closeNewBasketModal = () => {
    addBasketForm.resetFields();
    setIsBasketModalOpen(false);
    setIsConfirmationOpen(false);
  };

  const cancelConfirmation = () => {
    setIsConfirmationOpen(false);
  };

  const cancelDeleteConfirmation = () => {
    setIsDeleteConfirmationOpen(false);
  };

  if (loading) {
    return (
      <div className="accreditation-container">
        <div className="accreditation-counts">
          <div className="total-accreditation-container">
            <div className="total-accreditation total-back-bg">
              <div className="total-accreditation-text">
                <span>{t('loadingBaskets')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmModelComponent
        isOpen={isConfirmationOpen}
        onClickedCancel={cancelConfirmation}
        header={isEdit ? t('confirmUpdateTitle') : t('confirmCreateTitle')}
        content={<p>{isEdit ? t('confirmUpdateMessage') : t('confirmCreateMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleCreate,
            isDisabled: isConfirmClicked,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelConfirmation,
          },
        }}
        styleClasses="!w-1/4 min-w-[360px] md:min-w-[380px]"
      />

      <ConfirmModelComponent
        isOpen={isDeleteConfirmationOpen}
        onClickedCancel={cancelDeleteConfirmation}
        header={t('confirmDeleteTitle')}
        content={<p>{t('confirmDeleteMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleDelete,
            isDisabled: isConfirmClicked,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelDeleteConfirmation,
          },
        }}
        styleClasses="!w-11/12 sm:!w-3/4 md:!w-2/3 lg:!w-1/2 xl:!w-2/5 2xl:!w-1/3 min-w-[280px] max-w-[600px] [&_.modal-header]:pr-8 sm:[&_.modal-header]:pr-12"
      />

      <ConfirmModelComponent
        isOpen={isRestoreConfirmationOpen}
        onClickedCancel={cancelRestoreConfirmation}
        header={t('confirmRestoreTitle')}
        content={<p>{t('confirmRestoreMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleRestore,
            isDisabled: isConfirmClicked,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelRestoreConfirmation,
          },
        }}
        styleClasses="!w-1/4 min-w-[360px] md:min-w-[380px]"
      />

      <ProductAssignmentsModal
        open={isAssignmentsModalOpen}
        basket={selectedAssignmentBasket}
        loading={isAssignmentsLoading}
        onCancel={closeAssignmentsModal}
      />

      <FormModelComponent
        isOpen={isBasketModalOpen}
        header={isEdit ? t('updateBasketTitle') : t('createBasketTitle')}
        subheader={isEdit ? t('updateBasketSubTitle') : t('createBasketSubTitle')}
        content={
          <AddBasketForm
            form={addBasketForm}
            handleCreate={handleCreate}
            handleCancel={handleCancel}
            getConfirmation={getConfirmation}
            isModal={true}
            isBasketModalOpen={isBasketModalOpen}
            isEdit={isEdit}
            isEditDataLoading={isEditDataLoading}
          />
        }
        onClickedCancel={closeNewBasketModal}
        footerAction={{
          confimation: {
            label: `${isEdit ? t('updateBasket') : t('createBasket')}`,
            onClick: getConfirmation,
          },
          cancel: {
            label: t('cancel'),
            onClick: handleCancel,
          },
        }}
        styleClasses="!w-1/2 min-w-[360px] md:min-w-[380px] z-10"
        contentStyles="mb-4 px-2 max-h-[60vh] md:max-h-[70vh] overflow-y-auto space-y-2"
      />

      <div className="mt-4 flex w-full flex-col justify-start gap-4 md:flex-row md:items-center md:justify-between">
        <div className="items-start">
          <h2 className="text-xl font-medium text-headings-font-color">{t('basketMainHeading')}</h2>
          <p className="text-base font-medium text-font-color">{t('basketSubHeading')}</p>
        </div>

        {canManageBaskets && (
          <div className="flex justify-end">
            <PrimarySquareBtn handleOnClick={handleNewBasketButton} disabled={!isOnline}>
              <div className="flex">
                <Plus className="h-6 w-6 text-white" />
                <p className="pt-1">{t('newBasket')}</p>
              </div>
            </PrimarySquareBtn>
          </div>
        )}
      </div>

      <div className="my-6">
        <section className="mb-8 w-full">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">{t('filters')}</h3>
              <Button
                type="link"
                onClick={clearAllFilters}
                className="hover:text-primary-dark text-sm text-primary"
                disabled={!enableClearFiltersBtn}
              >
                {t('clearFilters')}
              </Button>
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">{t('baskets')}</label>
                </div>
                <Input
                  className="w-full rounded-lg"
                  placeholder={t('basketsFilterPlaceholder')}
                  allowClear
                  value={basketSearch}
                  onChange={(event) => setBasketSearch(event.target.value)}
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">{t('presentations')}</label>
                </div>
                <Select
                  className="w-full [&>.ant-select-selector]:!rounded-lg"
                  placeholder={t('presentationFilterPlaceholder')}
                  mode="multiple"
                  allowClear
                  onClear={() => setSelectedProducts([])}
                  value={selectedProducts}
                  maxTagCount={1}
                  maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 15 : 10}
                  onChange={setSelectedProducts}
                  loading={isLoadingFilterData}
                  options={products.map((product: any) => ({
                    label: product.name,
                    value: product.refId,
                  }))}
                  showSearch
                  filterOption={(input, option) =>
                    normalize(option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(normalize(input.toLowerCase()))
                  }
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">{t('statusFilter')}</label>
                </div>
                <div title={!isOnline ? t('offlineStatusInfoTitle') : undefined}>
                  <Select
                    className="w-full [&>.ant-select-selector]:!rounded-lg"
                    placeholder={t('statusFilterPlaceholder')}
                    value={statusFilter}
                    disabled={!isOnline}
                    onChange={setStatusFilter}
                    options={Object.values(ActiveFilterEnum).map((status) => ({
                      label: t(`common:statusFilter.${status}`),
                      value: status,
                    }))}
                  />
                </div>
              </Col>
            </Row>
          </div>
        </section>

        <div className="my-6">
          <BasketTable
            baskets={basketData}
            loading={isLoadingBaskets}
            isOnline={isOnline}
            totalBaskets={total}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onDelete={getDeleteConfirmation}
            onViewProductAssignments={handleViewProductAssignments}
            onRestore={getRestoreConfirmation}
            statusFilter={statusFilter}
          />
        </div>
      </div>

      <div className="accreditation-container">
        <div className="accreditation-counts">
          {!authStatus.isAuthenticated && (
            <Alert
              message="Authentication Required"
              description="You need to be logged in to sync data with the server. Please log in to enable sync functionality."
              type="warning"
              showIcon
              style={{ marginBottom: '20px' }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default BasketView;
